import { Component, OnInit, OnDestroy, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { init, ZAPICallClient } from "@z-api/call";
import { CallTokenService } from "../services/call-token.service";

type AppStatus = "loading" | "ready" | "error" | "invalid-url";

@Component({
  selector: "app-auth",
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Loading -->
    <div class="container" *ngIf="status === 'loading'">
      <div class="card">
        <div class="spinner"></div>
        <p class="label">Conectando ao servidor de chamadas...</p>
      </div>
    </div>

    <!-- Pronto -->
    <div class="container" *ngIf="status === 'ready'">
      <div class="card">
        <div class="icon-ok">✓</div>
        <h2>Webphone Ativo</h2>
        <p class="label">
          Instância: <strong>{{ instanceId }}</strong>
        </p>
        <p class="hint">O widget de chamadas está ativo no canto da tela.</p>
      </div>
    </div>

    <!-- Erro -->
    <div class="container" *ngIf="status === 'error'">
      <div class="card card--error">
        <div class="icon-err">✕</div>
        <h2>Falha na conexão</h2>
        <p class="label">{{ errorMessage }}</p>
        <button class="btn-retry" (click)="init()">Tentar novamente</button>
      </div>
    </div>

    <!-- URL inválida -->
    <div class="container" *ngIf="status === 'invalid-url'">
      <div class="card card--error">
        <div class="icon-err">!</div>
        <h2>URL inválida</h2>
        <p class="label">
          Acesse via:
          <code>/auth/[idInstancia]/[authToken]</code>
        </p>
      </div>
    </div>
  `,
  styles: [
    `
      .container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0d1117;
        padding: 1rem;
      }

      .card {
        background: #161b22;
        border: 1px solid #30363d;
        border-radius: 16px;
        padding: 2.5rem 3rem;
        text-align: center;
        max-width: 400px;
        width: 100%;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      }

      .card--error {
        border-color: #f44336;
      }

      h2 {
        font-size: 1.4rem;
        font-weight: 600;
        color: #e9edef;
        margin-bottom: 0.5rem;
      }

      .label {
        color: #8696a0;
        font-size: 0.95rem;
        margin-top: 0.5rem;
      }

      .hint {
        color: #8696a0;
        font-size: 0.85rem;
        margin-top: 1rem;
      }

      strong {
        color: #00a884;
      }

      code {
        background: #0d1117;
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        font-size: 0.85rem;
        color: #79c0ff;
      }

      /* Spinner */
      .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #30363d;
        border-top-color: #00a884;
        border-radius: 50%;
        animation: spin 0.9s linear infinite;
        margin: 0 auto 1.25rem;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      /* Ícones */
      .icon-ok {
        width: 52px;
        height: 52px;
        background: #00a884;
        color: #fff;
        font-size: 1.6rem;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.25rem;
      }

      .icon-err {
        width: 52px;
        height: 52px;
        background: #f44336;
        color: #fff;
        font-size: 1.6rem;
        font-weight: 700;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.25rem;
      }

      /* Botão retry */
      .btn-retry {
        margin-top: 1.25rem;
        padding: 0.65rem 1.5rem;
        background: #00a884;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 0.95rem;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-retry:hover {
        background: #008f70;
      }
    `,
  ],
})
export class AuthComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private callTokenService = inject(CallTokenService);

  status: AppStatus = "loading";
  instanceId = "";
  errorMessage = "";

  private client: ZAPICallClient | null = null;
  private tokenData: { instancia: string; token: string } | null = null;

  ngOnInit(): void {
    const idInstancia = this.route.snapshot.paramMap.get("idInstancia");
    const authToken = this.route.snapshot.paramMap.get("authToken");

    if (!idInstancia || !authToken) {
      this.status = "invalid-url";
      return;
    }

    this.instanceId = idInstancia;

    // Armazena as credenciais no serviço
    this.callTokenService.setCredentials(idInstancia, authToken);

    // Inicializa o widget
    this.init();
  }

  async init(): Promise<void> {
    this.status = "loading";
    this.errorMessage = "";

    // Destrói instância anterior, se houver
    this.client?.destroy();
    this.client = null;

    try {
      // Obtém o token e instancia
      this.tokenData = await this.callTokenService.getCallToken();
      this.instanceId = this.tokenData.instancia;

      this.client = init({
        instanceId: this.instanceId,

        // A SDK chama esta função sempre que precisar de um token (conexão/reconexão)
        getToken: () => Promise.resolve(this.tokenData!.token),

        position: "bottom-right",
        theme: { mode: "dark" },

        onIncomingCall: (call) => {
          console.log("[Z-API] Chamada recebida de:", call.from);
        },

        onCallTerminated: (callId) => {
          console.log("[Z-API] Chamada encerrada:", callId);
        },

        onConnectionChange: (connected) => {
          console.log("[Z-API] Conexão:", connected ? "online" : "offline");
        },
      });

      // Aguarda o evento de conexão para confirmar status
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Tempo limite de conexão excedido."));
        }, 15_000);

        this.client!.on("connected", () => {
          clearTimeout(timeout);
          resolve();
        });

        this.client!.on("error", (err: Error) => {
          clearTimeout(timeout);
          reject(err);
        });
      });

      this.status = "ready";
    } catch (err: unknown) {
      this.status = "error";
      this.errorMessage =
        err instanceof Error ? err.message : "Erro desconhecido ao conectar.";
      console.error("[Z-API] Erro ao inicializar:", err);
    }
  }

  ngOnDestroy(): void {
    this.client?.destroy();
  }
}
